/**
 * useDockOrchestration — the bottom-dock state + action wiring.
 *
 * One cohesive concern: everything the `<Dock>` and the cellular-warning
 * `Dialog` bind. That spans four families:
 *
 *   - The routed view-ref bridge — `surveyRef` + `setViewRef`, the template
 *     ref the RouterView assigns so the dock can read the survey wizard's
 *     exposed step state.
 *   - The dock-action handlers — sourced from `useAppNavigation`
 *     (`onDockForward`, `onDockBack`, `onDockStop`, `onDockRetake`) plus the
 *     cellular-preflight `onDockStart` that wraps the raw nav start.
 *   - The dock-prop computeds — `currentView` is threaded in (the route
 *     concern owns the bucket); the dock reads `speedtestStatus`,
 *     `isTestOnly`, `canGoBack`, `surveyState`.
 *   - The cellular warning — `showCellularWarning` + `onCellularWarningProceed`,
 *     the preflight gate the dialog binds.
 *
 * `provide(DockStartKey/DockForwardKey, …)` runs here: those keys carry
 * closures over THIS concern's cellular-warning policy + the nav forward
 * handler, so they're provided where they're born (per `keys.ts`'s note —
 * "closures over App.vue's cellular-warning policy"). `provide()` runs in
 * setup scope because the composable is called from App.vue's `<script
 * setup>`.
 *
 * `nav` is returned verbatim so App.vue can thread it into the DEV-only
 * `useSpeedtestDevHandle(nav)` install.
 */

import { computed, ref, provide, unref, type ComputedRef } from "vue";

import { useAppNavigation } from "@src/composables/useAppNavigation.ts";
import { DockStartKey, DockForwardKey } from "@src/composables/keys.ts";
import type { useConnectionType } from "@src/composables/useConnectionType.ts";

import { useSpeedtestStore } from "@features/speedtest";
import { useVariantStore } from "@src/stores/useVariantStore.ts";

import type { AppView } from "@src/composables/useRouteTransition.ts";

/**
 * The dock's per-step state contract. Structurally identical to the
 * `<Dock>` prop of the same name (`components/Dock.vue`); declared here
 * rather than imported from the component because the internal-boundary
 * rule keeps composables off the component layer (composables are
 * leaf-ish; components consume composables, not the inverse). TS is
 * structural, so the object this concern's `surveyState` computed
 * produces satisfies the `<Dock>` prop at App.vue's binding site without
 * a nominal cross-layer import. */
export interface SurveyDockState {
    isFirstStep: boolean;
    isLastStep: boolean;
    editingFromReview: boolean;
    /** True when the current step passes validation and the user may advance.
     *  Threaded from `useSurvey.canAdvance` so the dock's Next CTA can
     *  express `data-tier="primary"` (live action) vs quiet (not-yet). */
    canAdvance: boolean;
}

interface DockOrchestrationOptions {
    /** The route-derived view bucket — owned by `useRouteTransition`. */
    currentView: ComputedRef<AppView>;
    /** The app-level connection-type provider — owned by `useAppProviders`.
     *  Read for the cellular preflight on dock-start. */
    connectionType: ReturnType<typeof useConnectionType>;
}

export function useDockOrchestration(opts: DockOrchestrationOptions) {
    const { currentView, connectionType } = opts;

    const speedtestStore = useSpeedtestStore();
    const variantStore = useVariantStore();

    // ── Routed view-ref bridge ────────────────────────────────────────
    // The RouterView assigns the active component instance here; only the
    // survey route's instance is retained (the dock reads the wizard's
    // exposed step state through it).
    const surveyRef = ref<any>(null);
    function setViewRef(el: any) {
        surveyRef.value = currentView.value === "survey" ? el : null;
    }

    // ── Navigation (bottom-dock controls + router-based view state) ──────
    const nav = useAppNavigation({ surveyRef });
    const {
        onDockStart: _rawDockStart,
        onDockForward,
        onDockBack,
        onDockStop,
        onDockRetake,
    } = nav;

    // ── Dock-prop computeds ──────────────────────────────────────────────
    //
    // The dock is the only consumer of these shapes, so the computeds live
    // where they're read.

    const speedtestStatus = computed(() => ({
        isRunning: speedtestStore.isRunning,
        isCompleted: speedtestStore.currentPhase === "complete",
        currentPhase: speedtestStore.currentPhase,
    }));

    const isTestOnly = computed(
        () => variantStore.variant.surveyMode === "none",
    );

    const canGoBack = computed(() => {
        const view = currentView.value;
        if (view === "speedtest") {
            return (
                speedtestStore.isRunning ||
                speedtestStore.currentPhase === "complete"
            );
        }
        return view === "survey" || view === "thankyou" || view === "dashboard";
    });

    const surveyState = computed<SurveyDockState | undefined>(() => {
        if (currentView.value !== "survey") return undefined;
        const wizard = surveyRef.value;
        const survey = wizard?.survey;
        if (!survey) return undefined;
        return {
            isFirstStep: unref(survey.isFirstStep),
            isLastStep: unref(survey.isLastStep),
            editingFromReview: unref(wizard?.editingFromReview) ?? false,
            // D9 — thread `canAdvance` from `useSurvey` so the dock's Next
            // CTA expresses `data-tier="primary"` (live action, gold shimmer)
            // only when the current step passes validation. The prior wiring
            // dropped this ref entirely, leaving Next in a perpetual muted-
            // grey paint regardless of step completion.
            canAdvance: unref(survey.canAdvance) ?? true,
        };
    });

    // ── Cellular warning ──────────────────────────────────────────────────

    const showCellularWarning = ref(false);

    /** Intercept dock-start to show the cellular warning before any session work. */
    async function onDockStart() {
        if (connectionType.isCellular.value) {
            showCellularWarning.value = true;
            return;
        }
        _rawDockStart();
    }

    function onCellularWarningProceed() {
        showCellularWarning.value = false;
        _rawDockStart();
    }

    // Provide the start flow to the routed speedtest surface. The forward
    // handler carries the post-test → /survey route the old onDockNext held;
    // the bottom-dock's complete-state forward action reads the provided
    // handler via inject so test-only and survey variants share one policy seam.
    provide(DockStartKey, onDockStart);
    provide(DockForwardKey, onDockForward);

    return {
        // routed view-ref bridge
        setViewRef,
        // dock-prop computeds
        speedtestStatus,
        isTestOnly,
        canGoBack,
        surveyState,
        // dock-action handlers
        onDockStart,
        onDockForward,
        onDockBack,
        onDockStop,
        onDockRetake,
        // cellular warning
        showCellularWarning,
        onCellularWarningProceed,
        // dev-handle install seam
        nav,
    };
}
